<script lang="ts">
  import { connectionState, currentProjectMeta, projectMetas, projectStore, t } from '@dabble/app';
  import { Connection } from '@dabble/data/connection';
  import { persisted } from '@dabble/data/device';
  import { ERROR, PENDING, SAVED, SAVING, SaveStatus } from '@dabble/data/stores/project';
  import { isUnsyncedMeta } from '@dabble/data/stores/project-metas';
  import Dropdown from '@dabble/toolkit/Dropdown.svelte';
  import Icon from '@dabble/toolkit/Icon.svelte';
  import { mdiContentSaveEdit } from '@dabble/toolkit/custom-icons';
  import { br, escape } from '@dabble/toolkit/helpers';
  import { tooltipBottom } from '@dabble/toolkit/tooltip';
  import {
    mdiAlert,
    mdiClose,
    mdiCloud,
    mdiCloudAlert,
    mdiCloudArrowUp,
    mdiCloudCheck,
    mdiCloudOff,
    mdiCloudSync,
    mdiContentSaveAlert,
    mdiContentSaveCheck,
  } from '@mdi/js';
  import { syncState } from '../syncing';

  export let showOne = false;
  let showErrorMessage = false;

  const saveStatus = projectStore.status;
  const state = connectionState;

  $: needsSync =
    ($projectStore.project && isUnsyncedMeta($currentProjectMeta)) ||
    Object.values($projectMetas || {}).some(m => m && !m.deleted && isUnsyncedMeta(m));
  $: localPath = getIconPath($saveStatus);
  $: cloudIcon = getCloudIcon($state, $syncState, needsSync);
  $: localMessage = getLocalMessage($saveStatus);
  $: cloudMessage = getCloudMessage($state, $syncState, needsSync);
  $: hasError = $saveStatus === 'error' || $syncState instanceof Error;
  $: showErrorMessage = hasError;

  function getIconPath(saveStatus: SaveStatus) {
    if (saveStatus === ERROR) return mdiContentSaveAlert;
    if (saveStatus === PENDING) return mdiContentSaveEdit;
    if (saveStatus === SAVING) return mdiContentSaveEdit;
    if (saveStatus === SAVED) return mdiContentSaveCheck;
  }

  function getCloudIcon(state: Connection, syncing: boolean | Error, needsSync: boolean) {
    if (!state.authed) return mdiCloudOff;
    if (syncing instanceof Error) return mdiCloudAlert;
    if (syncing) return mdiCloudSync;
    if (needsSync) return mdiCloudArrowUp;
    return mdiCloudCheck;
  }

  function getLocalMessage(saveStatus: SaveStatus) {
    return $t('save_' + saveStatus);
  }

  function getCloudMessage(state: Connection, syncing: boolean | Error, needsSync: boolean) {
    if (!state.authed) return $t('syncing_offline');
    if (syncing instanceof Error) return $t('syncing_error', { error: syncing.message });
    if (needsSync) return $t('syncing_off');
    if (syncing) return $t('syncing_syncing');
    return $t('syncing_complete');
  }
</script>

<div class="sync-status">
  {#if !showOne || !$state.authed}
    <div class="status-icon" use:tooltipBottom={localMessage}>
      <Icon path={localPath} />
    </div>
    {#if !$persisted && !$state.authed}
      <div class="separator" />
      <div class="status-icon offline-unsupported" use:tooltipBottom={$t('save_offline_unsupported')}>
        <Icon path={mdiAlert} />
      </div>
    {/if}
  {/if}
  {#if !showOne}
    <div class="separator" />
  {/if}
  {#if !showOne || $state.authed}
    <div class="status-icon" use:tooltipBottom={cloudMessage}>
      <Icon
        path={cloudIcon === mdiCloudSync ? mdiCloud : cloudIcon}
        overlay={cloudIcon === mdiCloudSync ? 'cloudSync' : undefined}
        spin={cloudIcon === mdiCloudSync}
      />
    </div>
  {/if}
  {#if showErrorMessage}
    <Dropdown placement="top-start" arrow offset="0 8" noFocus={true}>
      <div class="error-box">
        <button class="icon close-btn" on:click={() => (showErrorMessage = false)}><Icon path={mdiClose} /></button>
        <h6>{$t('error')}</h6>
        <section>
          {@html br(
            escape(
              $t($syncState instanceof Error ? 'syncing_error_message' : 'save_error_message', {
                error: ($syncState instanceof Error && $syncState.message) || '',
              })
            )
          )}
        </section>
      </div>
    </Dropdown>
  {/if}
</div>

<style>
  .sync-status {
    color: var(--text-color-lighter);
    display: flex;
    align-items: center;
    padding: 0 4px;
    cursor: default;
  }
  .status-icon {
    display: grid;
    place-content: center;
    padding: 0.2em;
  }
  .separator {
    margin: 0 0.25rem;
    width: 1px;
    height: 20px;
    box-shadow: var(--vertical-bevel);
  }
  .error-box {
    position: relative;
    padding: 16px 12px;
    max-width: 300px;
    color: var(--text-color-light);
  }
  .icon.close-btn {
    position: absolute;
    right: 0;
    top: 4px;
    line-height: 1;
  }
  .error-box h6 {
    margin: 0 0 8px;
    font-weight: bold;
    text-transform: uppercase;
    font-size: var(--font-size-xs);
  }
  .error-box section {
    font-size: var(--font-size-sm);
  }
  .offline-unsupported {
    color: var(--brand-danger);
  }
</style>
